/**
 * User Context
 * 
 * Context provider for managing user authentication state.
 * Handles midway SSO integration and user alias management.
 */

import React, { createContext, useReducer, useContext, ReactNode } from 'react';

// Define the state interface
export interface UserState {
  userAlias: string | null;
  isLoading: boolean;
  error: string | null;
}

// Define action types
export type UserActionType =
  | { type: 'SET_USER_ALIAS'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' };

// Define the context interface
interface UserContextType {
  state: UserState;
  dispatch: React.Dispatch<UserActionType>;
}

// Initial state
const initialState: UserState = {
  userAlias: null,
  isLoading: false,
  error: null
};

// Create the context with undefined as default value to detect when used outside provider
export const UserContext = createContext<UserContextType | undefined>(undefined);

// Reducer function
const userReducer = (state: UserState, action: UserActionType): UserState => {
  switch (action.type) {
    case 'SET_USER_ALIAS':
      return {
        ...state,
        userAlias: action.payload,
        isLoading: false,
        error: null
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };

    default:
      return state;
  }
};

// Provider component
interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  return (
    <UserContext.Provider value={{ state, dispatch }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook for using the user context
export const useUserContext = () => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }

  return context;
};
